/**
 * Startseite.
 *
 * Zeigt den Stand der Stammdaten und weist auf offene Einrichtungspunkte
 * hin. Sobald Belege erfasst werden, kommen hier Umsatz, offene Posten
 * und Faelligkeiten dazu.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api, withQuery } from '../api/client';
import type { Company, Item, ListResponse, Partner } from '../api/types';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { Alert } from '../components/ui';

export function DashboardPage() {
  const { user, can } = useAuth();

  const customers = useQuery({
    queryKey: ['dashboard', 'customers'],
    queryFn: () =>
      api.get<ListResponse<Partner>>(withQuery('/api/partners', { role: 'customer', limit: 1 })),
  });

  const suppliers = useQuery({
    queryKey: ['dashboard', 'suppliers'],
    queryFn: () =>
      api.get<ListResponse<Partner>>(withQuery('/api/partners', { role: 'supplier', limit: 1 })),
  });

  const items = useQuery({
    queryKey: ['dashboard', 'items'],
    queryFn: () => api.get<ListResponse<Item>>(withQuery('/api/items', { limit: 1 })),
  });

  const lowStock = useQuery({
    queryKey: ['dashboard', 'low-stock'],
    queryFn: () =>
      api.get<ListResponse<Item>>(withQuery('/api/items', { belowMinStock: 'true', limit: 5 })),
  });

  const company = useQuery({
    queryKey: ['company'],
    queryFn: () => api.get<{ company: Company }>('/api/settings/company'),
    enabled: can('settings:read'),
  });

  // Angaben, ohne die eine Rechnung nicht den Anforderungen des § 14 UStG
  // und des § 35a GmbHG genuegt.
  const missing: string[] = [];
  const record = company.data?.company;
  if (record) {
    if (!record.vatId && !record.taxNumber) missing.push('USt-IdNr. oder Steuernummer');
    if (!record.street || !record.city) missing.push('vollständige Anschrift');
    if (!record.registerCourt || !record.registerNumber) missing.push('Registergericht und HRB-Nummer');
    if (!record.managingDirectors) missing.push('Geschäftsführer');
    if (!record.iban) missing.push('Bankverbindung');
  }

  return (
    <>
      <PageHeader
        title={`Guten Tag, ${user?.displayName ?? ''}`}
        subtitle={new Date().toLocaleDateString('de-DE', {
          weekday: 'long',
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })}
      />

      {missing.length > 0 ? (
        <Alert kind="warning">
          <div>
            <strong>Die Firmendaten sind noch unvollständig.</strong> Es fehlen: {missing.join(', ')}.
            Diese Angaben gehören auf jede Rechnung und jeden Geschäftsbrief.{' '}
            <Link to="/einstellungen">Jetzt ergänzen</Link>
          </div>
        </Alert>
      ) : null}

      <div className="tile-grid">
        <Tile label="Kunden" value={customers.data?.total} to="/kunden" />
        <Tile label="Lieferanten" value={suppliers.data?.total} to="/lieferanten" />
        <Tile label="Artikel" value={items.data?.total} to="/artikel" />
        <Tile
          label="Unter Meldebestand"
          value={lowStock.data?.total}
          to="/artikel"
          warning={(lowStock.data?.total ?? 0) > 0}
        />
      </div>

      {(lowStock.data?.items.length ?? 0) > 0 ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <h2>Nachbestellen</h2>
            <Link to="/artikel" className="btn btn-sm">
              Alle anzeigen
            </Link>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th className="shrink">Nummer</th>
                  <th>Bezeichnung</th>
                  <th className="numeric">Bestand</th>
                  <th className="numeric">Meldebestand</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.data!.items.map((item) => (
                  <tr key={item.id}>
                    <td className="mono">{item.number}</td>
                    <td>{item.name}</td>
                    <td className="numeric" style={{ color: 'var(--warning)', fontWeight: 600 }}>
                      {(item.stockQuantity / 1000).toLocaleString('de-DE')}
                    </td>
                    <td className="numeric muted">
                      {(item.minStock / 1000).toLocaleString('de-DE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Tile({
  label,
  value,
  to,
  warning,
}: {
  label: string;
  value: number | undefined;
  to: string;
  warning?: boolean;
}) {
  return (
    <Link to={to} className="tile" style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="tile-label">{label}</div>
      <div className="tile-value" style={warning ? { color: 'var(--warning)' } : undefined}>
        {value === undefined ? '—' : value}
      </div>
    </Link>
  );
}
